/**
 * Subscription headroom of a coding agent (20 Oct 2026, ADR 0049 decision 5 amendment): how much
 * of its usage windows (Claude Code's five-hour session and weekly limits) a machine's agent has
 * used, when each resets, and whether the vendor is refusing work now. Read only from documented
 * Claude Code surfaces, never a private endpoint or a credential file:
 *
 *   stream-json  `{ type: 'rate_limit_event', rate_limit_info: { status, resetsAt?, utilization?, rateLimitType? } }`
 *                from headless runs Orgus launches (`parseRateLimitEvent`). resetsAt's unit is not
 *                documented: seconds or milliseconds, told apart by magnitude (`epochMs`).
 *   /usage       `claude -p` with the prompt "/usage" may answer with a `usage_report` whose
 *                `rate_limits.limits[]` rows are { kind, percent, resets_at, scope.model.display_name,
 *                severity, is_active } (experimental): `usageReportWindows`. No report: nothing.
 *   statusline   `rate_limits: { five_hour: { used_percentage, resets_at }, seven_day: {...} }` in
 *                an interactive session's status line input (`statuslineWindows`; no Orgus path
 *                reads it yet).
 *   Codex        every rollout token_count's `rate_limits: { primary, secondary }` with used_percent
 *                and resets_at or resets_in_seconds (`codexRateLimitsUsage`, ORGU-193).
 *   Gemini CLI   an ACP error naming 429 RESOURCE_EXHAUSTED or an exhausted quota, reset at the
 *                retry delay it advertises (`quotaErrorUsage`, ORGU-193).
 *
 *   agent -> hub  { ext: 'agent-usage', type: 'usage',  data: AgentUsageReport }
 *   hub -> agent  { ext: 'agent-usage', type: 'config', data: { probe: boolean } } on connect and on change
 *
 * Routing reads it through `usagePressure` (agent-score.ts capacity term): no penalty under
 * USAGE_PRESSURE_FROM percent; above, a growing one, lighter when the window resets soon. A
 * 'rejected' status cools the pair until the reset (`usageCooling`, cause 'usage-limit').
 * Pure and browser-safe.
 */
import type { Id, Ts } from '../index.ts';
import { agentKindLabel } from './adapters.ts';
import type { AgentHealth } from './agent-health.ts';
import { COOLING_BASE_MS } from './agent-cooling.ts';

export const AGENT_USAGE_EXT = 'agent-usage';
export const AGENT_USAGE_TYPE = 'usage';
export const AGENT_USAGE_CONFIG_TYPE = 'config';
/**
 * hub -> agent, with an id: read usage now (a person pressed Refresh usage): the machine's own
 * sign-in, every signed-in account folder, and each token account in `tokens` (leased for this
 * read only, ADR 0088). Answered with 'probe.done' { read } once every read ended.
 */
export const AGENT_USAGE_PROBE_NOW_TYPE = 'probe.now';
export const AGENT_USAGE_PROBE_DONE_TYPE = 'probe.done';
export interface AgentUsageProbeNow {
  tokens?: { accountId: string; token: string }[];
}
/** How long a Refresh waits for a machine's reads (a /usage run each, one at a time). */
export const USAGE_REFRESH_TIMEOUT_MS = 90_000;
/** The /usage probe runs at most this often per machine. */
export const USAGE_PROBE_EVERY_MS = 15 * 60_000;
/** A report older than this is dropped (the agent probes every 15 minutes while it can). */
export const AGENT_USAGE_TTL_MS = 24 * 60 * 60_000;
/** No routing penalty below this percent used. */
export const USAGE_PRESSURE_FROM = 70;
/** A window resetting further away than this weighs in full. */
export const USAGE_FAR_RESET_MS = 6 * 60 * 60_000;
/** The lightest weight a window resetting now keeps. */
export const USAGE_NEAR_WEIGHT = 0.25;

export type AgentUsageStatus = 'allowed' | 'warning' | 'rejected' | 'unknown';
export type AgentUsageSeverity = 'normal' | 'warning' | 'limit';
export type AgentUsageSource = 'stream' | 'probe' | 'statusline';

/** One usage window, as the claude.ai usage panel lists it. */
export interface AgentUsageWindow {
  /** 'session', 'weekly_all', 'weekly_scoped', or what the agent reported. */
  kind: string;
  /** "Current session", "Weekly, all models", "Weekly, Opus". */
  label: string;
  /** Percent used, 0 to 100; null when only the status is known. */
  percent: number | null;
  resetsAt: Ts | null;
  /** The model a scoped window counts ("Opus"). */
  scope?: string;
  severity: AgentUsageSeverity;
}

/** The latest usage of one (machine, agent kind) pair. */
export interface AgentUsage {
  agentKind: string;
  machineId: Id;
  /**
   * The launch account the reading is for (ADR 0088): the probe runs in each signed-in account
   * folder too, and a run on a chosen account tags its events. Absent: the machine's own sign-in.
   */
  accountId?: string;
  windows: AgentUsageWindow[];
  status: AgentUsageStatus;
  /** When the vendor said it lifts a refusal ('rejected'), when known. */
  resetsAt?: Ts | null;
  at: Ts;
  source: AgentUsageSource;
}

/** What module 'agent-usage' offers: ctx.ext<AgentUsageApi>('agent-usage'). */
export interface AgentUsageApi {
  /** Every pair's latest usage younger than AGENT_USAGE_TTL_MS. */
  list(): AgentUsage[];
  /** Whether machines run the /usage probe. */
  probe(): boolean;
  /**
   * Read usage on one machine now (Refresh usage, ADR 0088), with token accounts' tokens for this
   * read only. Resolves when the machine answered or USAGE_REFRESH_TIMEOUT_MS passed.
   */
  refresh?(machineId: Id, tokens: { accountId: string; token: string }[]): Promise<{ ok: boolean; read: number; error?: string }>;
}

/** What an agent sends: the hub adds the machine and its own clock. */
export type AgentUsageReport = Omit<AgentUsage, 'machineId' | 'at'>;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);
const str = (v: unknown, max = 80): string | null => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null);

/**
 * An epoch in milliseconds from seconds, milliseconds or an ISO date: a number under 1e11 is
 * seconds (1e11 s is the year 5138; 1e11 ms is 1973). Null for anything else.
 */
export function epochMs(v: unknown): Ts | null {
  if (typeof v === 'string' && !/^\s*-?\d+(\.\d+)?\s*$/.test(v)) {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  const n = num(v);
  if (n === null || n <= 0) return null;
  return Math.round(n < 1e11 ? n * 1000 : n);
}

/** Percent used from 0 to 100; a fraction (0 to 1, as `utilization` may be) is scaled. */
function percentOf(v: unknown, fraction: boolean): number | null {
  const n = num(v);
  if (n === null || n < 0) return null;
  const p = fraction && n <= 1 ? n * 100 : n;
  return Math.round(Math.min(100, p) * 10) / 10;
}

const KIND_LABEL: Record<string, string> = {
  session: 'Current session',
  five_hour: 'Current session',
  weekly_all: 'Weekly, all models',
  seven_day: 'Weekly, all models',
  weekly_scoped: 'Weekly',
  daily: 'Today',
};
/** stream-json rateLimitType and statusline keys to the /usage kinds. */
const KIND_ALIAS: Record<string, string> = { five_hour: 'session', seven_day: 'weekly_all', seven_day_opus: 'weekly_scoped', seven_day_sonnet: 'weekly_scoped' };
const ALIAS_SCOPE: Record<string, string> = { seven_day_opus: 'Opus', seven_day_sonnet: 'Sonnet' };

/** A stream-json window key as a /usage kind: 'five_hour' session, 'seven_day' weekly, 'seven_day_<model>' that model's week. */
function aliasOf(key: string): { kind: string; scope: string | undefined } {
  if (KIND_ALIAS[key]) return { kind: KIND_ALIAS[key]!, scope: ALIAS_SCOPE[key] };
  const m = /^seven_day_([a-z0-9]+)$/i.exec(key);
  if (m) return { kind: 'weekly_scoped', scope: m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1).toLowerCase() };
  if (/^(one_day|daily|day)$/i.test(key)) return { kind: 'daily', scope: undefined };
  return { kind: key, scope: undefined };
}

function windowLabel(kind: string, scope: string | undefined): string {
  if (kind === 'weekly_scoped') return scope ? `Weekly, ${scope}` : 'Weekly, one model';
  return KIND_LABEL[kind] ?? kind.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function severityOf(v: unknown, percent: number | null): AgentUsageSeverity {
  const s = typeof v === 'string' ? v.toLowerCase() : '';
  if (/limit|reject|error|critical|exceed/.test(s) || (percent !== null && percent >= 100)) return 'limit';
  if (/warn|high/.test(s) || (percent !== null && percent >= 80)) return 'warning';
  return 'normal';
}

function statusOf(v: unknown): AgentUsageStatus {
  if (v === 'allowed') return 'allowed';
  if (v === 'allowed_warning') return 'warning';
  if (v === 'rejected') return 'rejected';
  return 'unknown';
}

/** One stream-json rate_limit_event, cleaned. */
export interface ClaudeRateLimit {
  status: AgentUsageStatus;
  resetsAt: Ts | null;
  /** Percent used, when the event carried utilization. */
  percent: number | null;
  /** The window it is about ('five_hour', 'seven_day', ...), when it said. */
  window: string | null;
  errorCode: string | null;
  /**
   * Every window the event carried (Claude Code 2.1.306 sends rate_limit_info.unifiedWindows,
   * { five_hour: { utilization, resetsAt }, seven_day: {...} }, on every request): checked 22 Oct 2026.
   */
  windows?: { window: string; percent: number | null; resetsAt: Ts | null }[];
}

/** A stream-json line (or its parsed object) as a rate limit event, or null when it is not one. */
export function parseRateLimitEvent(line: string | unknown): ClaudeRateLimit | null {
  let e: unknown = line;
  if (typeof line === 'string') {
    if (!line.includes('rate_limit_event')) return null;
    try {
      e = JSON.parse(line);
    } catch {
      return null;
    }
  }
  if (!isObj(e) || e.type !== 'rate_limit_event') return null;
  const info = isObj(e.rate_limit_info) ? e.rate_limit_info : {};
  const unified = isObj(info.unifiedWindows) ? info.unifiedWindows : isObj(info.unified_windows) ? info.unified_windows : null;
  const windows = unified
    ? Object.entries(unified)
        .slice(0, 12)
        .filter(([k, v]) => /^[a-z0-9_]{1,40}$/i.test(k) && isObj(v))
        .map(([k, v]) => ({ window: k, percent: percentOf((v as Record<string, unknown>).utilization, true), resetsAt: epochMs((v as Record<string, unknown>).resetsAt ?? (v as Record<string, unknown>).resets_at) }))
    : [];
  return {
    status: statusOf(info.status),
    resetsAt: epochMs(info.resetsAt ?? info.resets_at),
    percent: percentOf(info.utilization, true),
    window: str(info.rateLimitType ?? info.rate_limit_type, 40),
    errorCode: str(info.errorCode ?? info.error_code, 80),
    ...(windows.length ? { windows } : {}),
  };
}

// ── Codex and Gemini CLI (ORGU-193) ─────────────────────────────────────────

/** A Codex window's length in minutes as a /usage kind: 300 the session, 10080 the week. */
function codexKind(minutes: number | null, fallback: string): string {
  if (minutes === null) return fallback;
  if (minutes <= 300) return 'session';
  if (minutes >= 7 * 24 * 60 - 60 && minutes <= 7 * 24 * 60 + 60) return 'weekly_all';
  return fallback;
}

/**
 * The `rate_limits` block of a Codex rollout `token_count` event as a usage report:
 *   { primary: { used_percent, window_minutes, resets_at | resets_in_seconds }, secondary: {...} }
 * resets_at is an epoch (seconds or milliseconds); resets_in_seconds counts from `at`, the
 * line's timestamp. A window at 100 percent refuses work until its reset. Null when no window
 * names a percent.
 */
export function codexRateLimitsUsage(v: unknown, at: Ts, agentKind = 'codex'): AgentUsageReport | null {
  if (!isObj(v)) return null;
  const windows: AgentUsageWindow[] = [];
  for (const key of ['primary', 'secondary'] as const) {
    const w = v[key];
    if (!isObj(w)) continue;
    const percent = percentOf(w.used_percent ?? w.usedPercent, false);
    if (percent === null) continue;
    const minutes = num(w.window_minutes ?? w.windowMinutes);
    const inS = num(w.resets_in_seconds ?? w.resetsInSeconds);
    const resetsAt = epochMs(w.resets_at ?? w.resetsAt) ?? (inS !== null && inS >= 0 ? at + Math.round(inS * 1000) : null);
    const kind = codexKind(minutes, key === 'primary' ? 'session' : 'weekly_all');
    windows.push({ kind, label: windowLabel(kind, undefined), percent, resetsAt, severity: severityOf(null, percent) });
  }
  if (!windows.length) return null;
  const full = windows.filter((w) => w.severity === 'limit');
  const status: AgentUsageStatus = full.length ? 'rejected' : windows.some((w) => w.severity === 'warning') ? 'warning' : 'allowed';
  const resets = full.map((w) => w.resetsAt).filter((t): t is Ts => t !== null);
  const resetsAt = resets.length ? Math.max(...resets) : null;
  return { agentKind, windows, status, ...(resetsAt ? { resetsAt } : {}), source: 'stream' };
}

/** A retry delay a quota error names: "retryDelay": "37s", "retry in 37.5s", Retry-After: 60, "reset after 1h2m3s". */
function retryDelayMs(text: string): number | null {
  const m =
    /retry[_-]?delay["']?\s*[:=]\s*["']?(\d+(?:\.\d+)?)\s*s/i.exec(text) ??
    /retry[- ]after["']?\s*[:=]?\s*["']?(\d+(?:\.\d+)?)\s*(?:s\b|sec|seconds?\b|["',}\s]|$)/i.exec(text) ??
    /(?:retry|try again) in (\d+(?:\.\d+)?)\s*(?:s\b|sec|seconds?)/i.exec(text);
  if (m) return Math.round(Number(m[1]) * 1000);
  const hms = /(?:reset|retry|try again)\w*\s+(?:after|in)\s+((?:\d+(?:\.\d+)?\s*[hms]\s*){1,3})/i.exec(text);
  if (!hms) return null;
  let ms = 0;
  for (const p of hms[1]!.matchAll(/(\d+(?:\.\d+)?)\s*([hms])/gi)) ms += Number(p[1]) * (p[2]!.toLowerCase() === 'h' ? 3_600_000 : p[2]!.toLowerCase() === 'm' ? 60_000 : 1000);
  return ms > 0 ? Math.round(ms) : null;
}

/**
 * A quota error an ACP agent answered with (Gemini CLI: 429 RESOURCE_EXHAUSTED, "Quota exceeded")
 * as a refusing usage report, reset at the delay the error advertises (none: the generic cooling
 * applies). Null when the text is not a quota error.
 */
export function quotaErrorUsage(text: string, agentKind: string, now: Ts, accountId?: string | null): AgentUsageReport | null {
  if (!/\b429\b|RESOURCE_EXHAUSTED|quota (?:exceeded|exhausted)|exceeded your current quota|exhausted your (?:capacity|quota)|rate limit/i.test(text)) return null;
  const delay = retryDelayMs(text);
  const resetsAt = delay !== null && delay > 0 ? now + delay : null;
  return {
    agentKind,
    ...(accountId ? { accountId } : {}),
    windows: [{ kind: 'quota', label: 'Quota', percent: 100, resetsAt, severity: 'limit' }],
    status: 'rejected',
    ...(resetsAt ? { resetsAt } : {}),
    source: 'stream',
  };
}

/** What one rate limit event says about the agent's usage. */
export function usageFromRateLimit(r: ClaudeRateLimit, agentKind = 'claude-code', accountId?: string | null): AgentUsageReport {
  const windows: AgentUsageWindow[] = [];
  if (r.windows?.length) {
    // Every window the event carried; the refusal (status) is the one rateLimitType names.
    for (const w of r.windows) {
      const { kind, scope } = aliasOf(w.window);
      const limited = r.status === 'rejected' && w.window === r.window;
      windows.push({ kind, label: windowLabel(kind, scope), percent: limited && w.percent === null ? 100 : w.percent, resetsAt: w.resetsAt, ...(scope ? { scope } : {}), severity: limited ? 'limit' : severityOf(null, w.percent) });
    }
  } else if (r.percent !== null || r.window) {
    const kind = (r.window && KIND_ALIAS[r.window]) || r.window || 'session';
    const scope = (r.window && ALIAS_SCOPE[r.window]) || undefined;
    windows.push({ kind, label: windowLabel(kind, scope), percent: r.percent ?? (r.status === 'rejected' ? 100 : null), resetsAt: r.resetsAt, ...(scope ? { scope } : {}), severity: r.status === 'rejected' ? 'limit' : r.status === 'warning' ? 'warning' : severityOf(null, r.percent) });
  }
  return { agentKind, ...(accountId ? { accountId } : {}), windows, status: r.status, ...(r.resetsAt ? { resetsAt: r.resetsAt } : {}), source: 'stream' };
}

/** Find `usage_report` anywhere in a parsed stream-json event (a few levels deep). */
function findReport(v: unknown, depth = 0): Record<string, unknown> | null {
  if (depth > 5 || typeof v !== 'object' || v === null) return null;
  if (isObj(v) && isObj(v.usage_report)) return v.usage_report;
  for (const x of Array.isArray(v) ? v : Object.values(v)) {
    const r = findReport(x, depth + 1);
    if (r) return r;
  }
  return null;
}

/**
 * The windows of a /usage answer: `usage_report.rate_limits.limits[]` in a stream-json line (or
 * its parsed object). Inactive rows are left out. Null when the line carries no report.
 */
export function usageReportWindows(line: string | unknown): AgentUsageWindow[] | null {
  let e: unknown = line;
  if (typeof line === 'string') {
    if (!line.includes('usage_report')) return null;
    try {
      e = JSON.parse(line);
    } catch {
      return null;
    }
  }
  const rep = findReport(e);
  const limits = rep && isObj(rep.rate_limits) && Array.isArray(rep.rate_limits.limits) ? rep.rate_limits.limits : null;
  if (!limits) return null;
  const out: AgentUsageWindow[] = [];
  for (const row of limits.slice(0, 20)) {
    if (!isObj(row) || row.is_active === false) continue;
    const kind = str(row.kind, 40);
    if (!kind) continue;
    const percent = percentOf(row.percent ?? row.used_percentage, false);
    const scope = isObj(row.scope) && isObj(row.scope.model) ? (str(row.scope.model.display_name, 40) ?? undefined) : undefined;
    out.push({ kind, label: windowLabel(kind, scope), percent, resetsAt: epochMs(row.resets_at ?? row.resetsAt), ...(scope ? { scope } : {}), severity: severityOf(row.severity, percent) });
  }
  return out;
}

/** The windows of a status line input's `rate_limits` (five_hour, seven_day, ...). */
export function statuslineWindows(rateLimits: unknown): AgentUsageWindow[] {
  if (!isObj(rateLimits)) return [];
  const out: AgentUsageWindow[] = [];
  for (const [key, w] of Object.entries(rateLimits).slice(0, 10)) {
    if (!isObj(w)) continue;
    const kind = KIND_ALIAS[key] ?? key;
    const scope = ALIAS_SCOPE[key];
    const percent = percentOf(w.used_percentage, false);
    out.push({ kind, label: windowLabel(kind, scope), percent, resetsAt: epochMs(w.resets_at), ...(scope ? { scope } : {}), severity: severityOf(null, percent) });
  }
  return out;
}

/** A Codex window length in minutes as a /usage kind: five hours a session, a week, a day; else `<n>m`. */
function codexWindowKind(minutes: number | null, key: string): string {
  if (minutes === 300) return 'session';
  if (minutes === 10080) return 'weekly_all';
  if (minutes === 1440) return 'daily';
  return minutes !== null && minutes > 0 ? `${Math.round(minutes)}m` : key;
}

/** Find `rate_limits` in a Codex event: top level, `payload` (rollout event_msg) or `msg` (older exec). */
function codexRateLimitsOf(e: unknown): Record<string, unknown> | null {
  if (!isObj(e)) return null;
  for (const o of [e, e.payload, e.msg, isObj(e.payload) ? e.payload.info : null]) {
    if (isObj(o) && isObj(o.rate_limits)) return o.rate_limits;
  }
  return null;
}

/**
 * What a Codex `token_count` event's `rate_limits` says (`{ primary: { used_percent,
 * window_minutes, resets_at | resets_in_seconds }, secondary: {...} }`), from a JSONL line, its
 * parsed object, or the rate_limits object itself. `resets_in_seconds` counts from `now`. Status:
 * 'rejected' when a window is full, 'warning' from 80 percent, else 'allowed'. Null when it
 * carries no window.
 */
export function usageFromCodexRateLimits(line: string | unknown, now: Ts, accountId?: string | null): AgentUsageReport | null {
  let e: unknown = line;
  if (typeof line === 'string') {
    if (!line.includes('rate_limits')) return null;
    try {
      e = JSON.parse(line);
    } catch {
      return null;
    }
  }
  const rl = codexRateLimitsOf(e) ?? (isObj(e) && (isObj(e.primary) || isObj(e.secondary)) ? e : null);
  if (!rl) return null;
  const windows: AgentUsageWindow[] = [];
  for (const key of ['primary', 'secondary']) {
    const w = rl[key];
    if (!isObj(w)) continue;
    const percent = percentOf(w.used_percent, false);
    const kind = codexWindowKind(num(w.window_minutes), key);
    const secs = num(w.resets_in_seconds);
    const resetsAt = epochMs(w.resets_at) ?? (secs !== null && secs >= 0 ? Math.round(now + secs * 1000) : null);
    windows.push({ kind, label: windowLabel(kind, undefined), percent, resetsAt, severity: severityOf(null, percent) });
  }
  if (!windows.length) return null;
  const full = windows.filter((w) => w.severity === 'limit');
  const status: AgentUsageStatus = full.length ? 'rejected' : windows.some((w) => w.severity === 'warning') ? 'warning' : 'allowed';
  const resets = full.map((w) => w.resetsAt).filter((t): t is Ts => t !== null);
  const resetsAt = resets.length ? Math.max(...resets) : null;
  return { agentKind: 'codex', ...(accountId ? { accountId } : {}), windows, status, ...(resetsAt ? { resetsAt } : {}), source: 'stream' };
}

const STATUSES = new Set<AgentUsageStatus>(['allowed', 'warning', 'rejected', 'unknown']);
const SOURCES = new Set<AgentUsageSource>(['stream', 'probe', 'statusline']);
const SEVERITIES = new Set<AgentUsageSeverity>(['normal', 'warning', 'limit']);

/** An agent's 'usage' frame, cleaned, with the hub's machine id and clock; null when it is not one. */
export function cleanAgentUsage(v: unknown, machineId: Id, now: Ts): AgentUsage | null {
  if (!isObj(v)) return null;
  const agentKind = str(v.agentKind, 64);
  if (!agentKind || !/^[a-z0-9][a-z0-9._-]*$/i.test(agentKind)) return null;
  const windows: AgentUsageWindow[] = [];
  for (const w of Array.isArray(v.windows) ? v.windows.slice(0, 20) : []) {
    if (!isObj(w)) continue;
    const kind = str(w.kind, 40);
    if (!kind) continue;
    const percent = percentOf(w.percent, false);
    const scope = str(w.scope, 40) ?? undefined;
    windows.push({
      kind,
      label: str(w.label, 60) ?? windowLabel(kind, scope),
      percent,
      resetsAt: epochMs(w.resetsAt),
      ...(scope ? { scope } : {}),
      severity: SEVERITIES.has(w.severity as AgentUsageSeverity) ? (w.severity as AgentUsageSeverity) : severityOf(null, percent),
    });
  }
  const status = STATUSES.has(v.status as AgentUsageStatus) ? (v.status as AgentUsageStatus) : 'unknown';
  const resetsAt = epochMs(v.resetsAt);
  const accountId = typeof v.accountId === 'string' && /^[\w-]{1,64}$/.test(v.accountId) ? v.accountId : null;
  return { agentKind, machineId, ...(accountId ? { accountId } : {}), windows, status, ...(resetsAt ? { resetsAt } : {}), at: now, source: SOURCES.has(v.source as AgentUsageSource) ? (v.source as AgentUsageSource) : 'stream' };
}

/**
 * The pair's usage after a new reading: windows the reading names replace the old ones of that
 * kind (and scope), the others stay until they reset. Status and resetsAt are the newest.
 */
export function mergeUsage(prev: AgentUsage | null | undefined, next: AgentUsage): AgentUsage {
  if (!prev) return next;
  const key = (w: AgentUsageWindow) => `${w.kind}\u0000${w.scope ?? ''}`;
  const named = new Set(next.windows.map(key));
  const kept = prev.windows.filter((w) => !named.has(key(w)) && (w.resetsAt === null || w.resetsAt > next.at));
  return { ...next, windows: [...next.windows, ...kept] };
}

/** Windows that have not reset yet (a window past its reset says nothing about now). */
export function liveWindows(u: Pick<AgentUsage, 'windows'>, now: Ts): AgentUsageWindow[] {
  return u.windows.filter((w) => w.resetsAt === null || w.resetsAt > now);
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "13:00" within a day, else "Wed 13:00", on the hub's clock or the given UTC offset. */
export function resetText(at: Ts, now: Ts, utcOffsetMinutes: number | null = null): string {
  const d = utcOffsetMinutes === null ? new Date(at) : new Date(at + utcOffsetMinutes * 60_000);
  const h = utcOffsetMinutes === null ? d.getHours() : d.getUTCHours();
  const m = utcOffsetMinutes === null ? d.getMinutes() : d.getUTCMinutes();
  const day = utcOffsetMinutes === null ? d.getDay() : d.getUTCDay();
  const clock = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return at - now < 20 * 60 * 60_000 ? clock : `${DAYS[day]} ${clock}`;
}

/** "weekly", "session", "weekly Opus": a window in a reason line. */
function windowWords(w: AgentUsageWindow): string {
  if (w.kind === 'session' || w.kind === 'five_hour') return 'session';
  if (w.kind === 'weekly_all' || w.kind === 'seven_day') return 'weekly';
  if (w.kind === 'weekly_scoped') return w.scope ? `weekly ${w.scope}` : 'weekly model';
  return w.label.toLowerCase();
}

/** How full the binding window is, as routing weighs it. */
export interface UsagePressure {
  /** 0 to 1: the share of the full penalty (agent-score.ts SPARE_PENALTY). */
  weight: number;
  window: AgentUsageWindow | null;
  /** "Claude Code weekly limit 83% used, resets Wed 13:00"; null under USAGE_PRESSURE_FROM. */
  text: string | null;
}

/**
 * The capacity weight of one pair's usage: 0 below USAGE_PRESSURE_FROM percent, rising to 1 at
 * 100 percent used; a window that resets within USAGE_FAR_RESET_MS weighs less, down to
 * USAGE_NEAR_WEIGHT as its reset nears. The binding window is the one that weighs most. A
 * 'rejected' status that still holds (usageRefused) weighs 1 (cooling skips the pair anyway).
 */
export function usagePressure(u: AgentUsage | null | undefined, now: Ts, utcOffsetMinutes: number | null = null): UsagePressure {
  if (!u) return { weight: 0, window: null, text: null };
  const label = agentKindLabel(u.agentKind);
  let best: UsagePressure = { weight: 0, window: null, text: null };
  for (const w of liveWindows(u, now)) {
    if (w.percent === null || w.percent < USAGE_PRESSURE_FROM) continue;
    const fill = Math.min(1, (w.percent - USAGE_PRESSURE_FROM) / (100 - USAGE_PRESSURE_FROM));
    const left = w.resetsAt === null ? Infinity : w.resetsAt - now;
    const soon = left >= USAGE_FAR_RESET_MS ? 1 : USAGE_NEAR_WEIGHT + (1 - USAGE_NEAR_WEIGHT) * Math.max(0, left / USAGE_FAR_RESET_MS);
    const weight = fill * soon;
    if (weight > best.weight || !best.window) {
      const reset = w.resetsAt !== null ? `, resets ${resetText(w.resetsAt, now, utcOffsetMinutes)}` : '';
      best = { weight, window: w, text: `${label} ${windowWords(w)} limit ${Math.round(w.percent)}% used${reset}` };
    }
  }
  if (usageRefused(u, now)) {
    const at = rejectedUntil(u, now);
    return { weight: 1, window: best.window, text: `${label} usage limit reached${at ? `, resets ${resetText(at, now, utcOffsetMinutes)}` : ''}` };
  }
  return best;
}

/**
 * Whether a 'rejected' reading still holds now: until its reset when one is known; with no reset
 * known, for COOLING_BASE_MS after the reading. A refusal past its reset says nothing about now, so
 * routing and the page stop treating the pair as refused (it lasted until the 24 hour TTL before).
 */
export function usageRefused(u: AgentUsage, now: Ts): boolean {
  if (u.status !== 'rejected') return false;
  if (rejectedUntil(u, now) !== null) return true;
  const known = (u.resetsAt ?? null) !== null || u.windows.some((w) => w.resetsAt !== null && (w.severity === 'limit' || (w.percent ?? 0) >= 100));
  return !known && now - u.at < COOLING_BASE_MS;
}

/** When a refused pair takes work again: the status's reset, else the latest full window's. */
export function rejectedUntil(u: AgentUsage, now: Ts): Ts | null {
  if (u.resetsAt && u.resetsAt > now) return u.resetsAt;
  const full = liveWindows(u, now).filter((w) => w.resetsAt !== null && (w.severity === 'limit' || (w.percent ?? 0) >= 100));
  return full.length ? Math.max(...full.map((w) => w.resetsAt as number)) : null;
}

/**
 * The health record a 'rejected' reading gives the pair (cause 'usage-limit'): cooling until the
 * reset, or COOLING_BASE_MS when no reset is known. Null for any other status.
 */
export function usageCooling(prev: AgentHealth | null | undefined, u: AgentUsage, now: Ts, utcOffsetMinutes: number | null = null): AgentHealth | null {
  if (u.status !== 'rejected') return null;
  const until = rejectedUntil(u, now) ?? now + COOLING_BASE_MS;
  const label = agentKindLabel(u.agentKind);
  return {
    machineId: u.machineId,
    kind: u.agentKind,
    coolUntil: Math.max(until, prev?.coolUntil ?? 0),
    strikes: (prev?.strikes ?? 0) + (prev?.lastCause === 'usage-limit' ? 0 : 1),
    lastCause: 'usage-limit',
    lastMessage: `${label} reached its usage limit; it resets ${resetText(until, now, utcOffsetMinutes)}.`,
    at: now,
  };
}

// ── /usage as text (Claude Code 2.1.306) ────────────────────────────────────

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** The UTC offset (ms) of an IANA zone at an instant, or null when the zone is unknown. */
function zoneOffset(at: Ts, zone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }).formatToParts(new Date(at));
    const v = (t: string) => Number(parts.find((p) => p.type === t)?.value);
    return Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second')) - Math.floor(at / 1000) * 1000;
  } catch {
    return null;
  }
}

/**
 * A reset as /usage writes it, "Oct 23, 1:09am (Asia/Dubai)" or "1:09am (Asia/Dubai)", as an
 * epoch: the wall time in that zone (the local zone when none is named), the next one after `now`.
 */
export function usageResetAt(text: string, now: Ts): Ts | null {
  const m = /^(?:([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:\(([A-Za-z_+\-/0-9]{1,40})\))?$/i.exec(text.trim());
  if (!m) return null;
  let hour = Number(m[3]);
  const minute = m[4] ? Number(m[4]) : 0;
  const ampm = m[5]?.toLowerCase();
  if (hour > 23 || minute > 59) return null;
  if (ampm === 'pm' && hour < 12) hour += 12;
  if (ampm === 'am' && hour === 12) hour = 0;
  const zone = m[6] ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = zoneOffset(now, zone);
  if (offset === null) return null;
  const local = new Date(now + offset);
  let year = local.getUTCFullYear();
  let month = local.getUTCMonth();
  let day = local.getUTCDate();
  if (m[1]) {
    const mi = MONTHS.indexOf(m[1].toLowerCase());
    if (mi < 0) return null;
    month = mi;
    day = Number(m[2]);
  }
  const at = (y: number) => Date.UTC(y, month, day, hour, minute) - (zoneOffset(Date.UTC(y, month, day, hour, minute), zone) ?? offset);
  let t = at(year);
  if (m[1] && t < now - 24 * 60 * 60_000) t = at(++year);
  if (!m[1] && t <= now) t += 24 * 60 * 60_000;
  return t;
}

/**
 * The windows of /usage's text answer: "Current session: 12% used · resets Oct 23, 1:09am
 * (Asia/Dubai)", "Current week (all models): ...", "Current week (Fable): ...", and a day window
 * should one appear. Only those lines are read; the rest of the answer is advice for a person.
 */
export function usageTextWindows(text: string, now: Ts): AgentUsageWindow[] {
  const out: AgentUsageWindow[] = [];
  const re = /^\s*Current (session|week|day)(?: \(([^)\n]{1,40})\))?:\s*(\d{1,3}(?:\.\d+)?)% used(?:\s*[·•|-]\s*resets?\s+([^\n]{1,60}))?\s*$/gim;
  for (const m of text.matchAll(re)) {
    const what = m[1]!.toLowerCase();
    const scopeRaw = m[2]?.trim();
    const all = !scopeRaw || /^all models?$/i.test(scopeRaw);
    const kind = what === 'session' ? 'session' : what === 'day' ? 'daily' : all ? 'weekly_all' : 'weekly_scoped';
    const scope = kind === 'weekly_scoped' ? scopeRaw : undefined;
    const percent = Math.min(100, Number(m[3]));
    out.push({ kind, label: windowLabel(kind, scope), percent, resetsAt: m[4] ? usageResetAt(m[4], now) : null, ...(scope ? { scope } : {}), severity: severityOf(null, percent) });
    if (out.length >= 12) break;
  }
  return out;
}
